// Choosing the part of an image that stands for someone or something (an
// avatar, a workspace's icon): the image under a round window, moved by
// dragging, sized by the slider, the wheel or a pinch, and cut to a small
// square on the device before it goes up.
//
// Promise:
// - The window is always covered: the image cannot be moved or shrunk so that
//   the circle shows anything but the image.
// - What is inside the circle's square is what is kept (the screens round it
//   or not), at most PICTURE_OUTPUT_PX on a side and never enlarged.
// - The position is kept relative to the window, so a stage that changes size
//   (a phone turned) keeps the same part.
// - The keyboard does what the pointer does: arrows move, + and - resize.
import { m } from "@trameria/i18n";
import {
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { ErrorLine } from "./bits";
import { Button } from "./button";
import { Modal } from "./modal";

/** The side of the square that goes up, in px. */
export const PICTURE_OUTPUT_PX = 512;
const MAX_ZOOM = 4;
/** The room around the circle inside the stage, in px. */
const MARGIN = 20;

/**
 * Where the image sits under the window. `zoom` 1 makes its shorter side the
 * window's diameter; `x` and `y` move its center from the window's, in
 * diameters.
 */
export interface CropView {
  zoom: number;
  x: number;
  y: number;
}

export interface Size {
  w: number;
  h: number;
}

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

/** Diameters per pixel of the image at `zoom`. */
const unitsPerPx = (n: Size, zoom: number) => zoom / Math.min(n.w, n.h);

/** The view moved and sized back so the image covers the window. */
export function clampView(v: CropView, n: Size): CropView {
  const zoom = clamp(v.zoom, 1, MAX_ZOOM);
  const k = unitsPerPx(n, zoom);
  const mx = (n.w * k - 1) / 2;
  const my = (n.h * k - 1) / 2;
  return { zoom, x: clamp(v.x, -mx, mx), y: clamp(v.y, -my, my) };
}

/**
 * The view sized to `zoom` around `focus` (a point in diameters from the
 * window's center, which stays over the same part of the image).
 */
export function zoomView(
  v: CropView,
  n: Size,
  zoom: number,
  focus = { x: 0, y: 0 },
): CropView {
  const next = clamp(zoom, 1, MAX_ZOOM);
  const r = next / v.zoom;
  return clampView(
    {
      zoom: next,
      x: focus.x - (focus.x - v.x) * r,
      y: focus.y - (focus.y - v.y) * r,
    },
    n,
  );
}

/** The square of the image (in its pixels) the window shows. */
export function cropRect(
  v: CropView,
  n: Size,
): { sx: number; sy: number; size: number } {
  const k = unitsPerPx(n, v.zoom);
  const size = 1 / k;
  return {
    sx: n.w / 2 - v.x / k - size / 2,
    sy: n.h / 2 - v.y / k - size / 2,
    size,
  };
}

const canvasBlob = (canvas: HTMLCanvasElement, type: string) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.9));

/**
 * The window's square drawn small: WebP where the browser writes it (it keeps
 * transparency and is small), else JPEG for a photo and PNG for the rest.
 */
async function renderCrop(
  img: HTMLImageElement,
  n: Size,
  v: CropView,
  sourceType: string,
): Promise<Blob> {
  const { sx, sy, size } = cropRect(v, n);
  const side = clamp(Math.round(size), 1, PICTURE_OUTPUT_PX);
  const canvas = document.createElement("canvas");
  canvas.width = side;
  canvas.height = side;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error(m.common_picture_failed());
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, size, size, 0, 0, side, side);
  const webp = await canvasBlob(canvas, "image/webp");
  if (webp?.type === "image/webp") return webp;
  const other = await canvasBlob(
    canvas,
    sourceType === "image/jpeg" ? "image/jpeg" : "image/png",
  );
  if (!other) throw new Error(m.common_picture_failed());
  return other;
}

/**
 * The cropper in a dialog, open while `file` is given. `onDone` gets the cut
 * square and keeps the dialog (its button held) until it settles; a failure
 * stays under the button.
 */
export function PictureCropModal({
  file,
  label,
  onCancel,
  onDone,
}: {
  file: File | null;
  /** What the picture is for (the dialog's title). */
  label: string;
  onCancel(): void;
  onDone(picture: Blob): Promise<void>;
}) {
  return (
    <Modal open={file !== null} onClose={onCancel} label={label}>
      {file && (
        <Cropper
          key={`${file.name}:${file.size}:${file.lastModified}`}
          file={file}
          label={label}
          onCancel={onCancel}
          onDone={onDone}
        />
      )}
    </Modal>
  );
}

function Cropper({
  file,
  label,
  onCancel,
  onDone,
}: {
  file: File;
  label: string;
  onCancel(): void;
  onDone(picture: Blob): Promise<void>;
}) {
  const [src] = useState(() => URL.createObjectURL(file));
  // frontend-development: not a fetch; lets go of the object URL.
  useEffect(() => () => URL.revokeObjectURL(src), [src]);
  const img = useRef<HTMLImageElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const [side, setSide] = useState(0);
  const [natural, setNatural] = useState<Size | null>(null);
  const [unreadable, setUnreadable] = useState(false);
  const [view, setView] = useState<CropView>({ zoom: 1, x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());

  // frontend-development: not a fetch; follows the stage's width.
  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    const measure = () => setSide(el.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const diameter = Math.max(1, side - MARGIN * 2);

  /** A point of the stage (client px) in diameters from the window's center. */
  const focusAt = (clientX: number, clientY: number) => {
    const box = stage.current?.getBoundingClientRect();
    if (!box) return { x: 0, y: 0 };
    return {
      x: (clientX - box.left - box.width / 2) / diameter,
      y: (clientY - box.top - box.height / 2) / diameter,
    };
  };

  // frontend-development: not a fetch; the wheel must be a listener that may
  // cancel (React's is passive), or the dialog scrolls behind.
  useEffect(() => {
    const el = stage.current;
    if (!el || !natural) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const focus = focusAt(e.clientX, e.clientY);
      setView((v) =>
        zoomView(v, natural, v.zoom * Math.exp(-e.deltaY * 0.002), focus),
      );
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  });

  const move = (dx: number, dy: number) => {
    if (!natural) return;
    setView((v) => clampView({ ...v, x: v.x + dx, y: v.y + dy }, natural));
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    const map = pointers.current;
    const before = map.get(e.pointerId);
    if (!before || !natural) return;
    const now = { x: e.clientX, y: e.clientY };
    if (map.size === 1) {
      map.set(e.pointerId, now);
      move((now.x - before.x) / diameter, (now.y - before.y) / diameter);
      return;
    }
    // Two fingers: the distance sizes, the midpoint moves.
    const other = [...map.entries()].find(([id]) => id !== e.pointerId)?.[1];
    map.set(e.pointerId, now);
    if (!other) return;
    const apart = (a: { x: number; y: number }) =>
      Math.hypot(a.x - other.x, a.y - other.y);
    const ratio = apart(before) > 0 ? apart(now) / apart(before) : 1;
    const mid = {
      x: (now.x + other.x) / 2,
      y: (now.y + other.y) / 2,
    };
    const shift = {
      x: (now.x - before.x) / 2 / diameter,
      y: (now.y - before.y) / 2 / diameter,
    };
    setView((v) => {
      const sized = zoomView(v, natural, v.zoom * ratio, focusAt(mid.x, mid.y));
      return clampView(
        { ...sized, x: sized.x + shift.x, y: sized.y + shift.y },
        natural,
      );
    });
  };

  const release = (e: ReactPointerEvent) => {
    pointers.current.delete(e.pointerId);
  };

  const apply = async () => {
    const el = img.current;
    if (!el || !natural || saving) return;
    setSaving(true);
    setFailed(null);
    try {
      await onDone(await renderCrop(el, natural, view, file.type));
    } catch (error) {
      setFailed(
        error instanceof Error && error.message
          ? error.message
          : m.common_picture_failed(),
      );
    } finally {
      setSaving(false);
    }
  };

  const k = natural ? unitsPerPx(natural, view.zoom) : 0;
  const shownW = natural ? natural.w * k * diameter : 0;
  const shownH = natural ? natural.h * k * diameter : 0;
  const left = side / 2 + view.x * diameter - shownW / 2;
  const top = side / 2 + view.y * diameter - shownH / 2;

  return (
    <div>
      <h2 className="text-[15px] font-normal text-fg">{label}</h2>
      <p className="mt-1 text-[12px] leading-[1.7] text-faint">
        {m.common_picture_crop_hint()}
      </p>
      {/* biome-ignore lint/a11y/useSemanticElements: a stage moved by keys and pointers, not a group of fields */}
      <div
        ref={stage}
        role="group"
        aria-label={m.common_picture_stage()}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: the arrows move the image
        tabIndex={0}
        data-picture-stage=""
        className="relative mx-auto mt-4 aspect-square w-full max-w-[360px] cursor-grab touch-none select-none overflow-hidden rounded-[10px] bg-black outline-none active:cursor-grabbing focus-visible:ring-2 focus-visible:ring-accent"
        onPointerDown={(e) => {
          if (!natural) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        }}
        onPointerMove={onPointerMove}
        onPointerUp={release}
        onPointerCancel={release}
        onKeyDown={(e) => {
          if (!natural) return;
          const step = e.shiftKey ? 0.1 : 0.02;
          const keys: Record<string, () => void> = {
            ArrowLeft: () => move(step, 0),
            ArrowRight: () => move(-step, 0),
            ArrowUp: () => move(0, step),
            ArrowDown: () => move(0, -step),
            "+": () => setView((v) => zoomView(v, natural, v.zoom * 1.1)),
            "=": () => setView((v) => zoomView(v, natural, v.zoom * 1.1)),
            "-": () => setView((v) => zoomView(v, natural, v.zoom / 1.1)),
          };
          const act = keys[e.key];
          if (!act) return;
          e.preventDefault();
          act();
        }}
      >
        <img
          ref={img}
          src={src}
          alt=""
          draggable={false}
          onLoad={(e) => {
            const el = e.currentTarget;
            if (el.naturalWidth > 0 && el.naturalHeight > 0)
              setNatural({ w: el.naturalWidth, h: el.naturalHeight });
            else setUnreadable(true);
          }}
          onError={() => setUnreadable(true)}
          className="pointer-events-none absolute top-0 left-0 max-w-none origin-top-left"
          style={{
            width: shownW,
            height: shownH,
            transform: `translate(${left}px, ${top}px)`,
            visibility: natural ? "visible" : "hidden",
          }}
        />
        {/* The window: everything around the circle is dimmed. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute rounded-full border border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]"
          style={{
            left: MARGIN,
            top: MARGIN,
            width: diameter,
            height: diameter,
          }}
        />
      </div>
      <div className="mx-auto mt-3 flex max-w-[360px] items-center gap-2.5">
        <ZoomMark small />
        <input
          type="range"
          min={1}
          max={MAX_ZOOM}
          step={0.01}
          value={view.zoom}
          disabled={!natural}
          aria-label={m.common_picture_zoom()}
          onChange={(e) => {
            if (natural)
              setView((v) => zoomView(v, natural, Number(e.target.value)));
          }}
          className="h-6 min-w-0 flex-1 cursor-pointer accent-accent"
        />
        <ZoomMark />
      </div>
      {unreadable && <ErrorLine>{m.common_picture_unreadable()}</ErrorLine>}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          {m.common_cancel()}
        </Button>
        {/* The label stays while saving (a word swapped in would change the
            width); the button is held and says so aloud. */}
        <Button
          variant="primary"
          disabled={!natural || saving}
          aria-busy={saving}
          onClick={() => void apply()}
        >
          {m.common_picture_apply()}
        </Button>
      </div>
      {failed && (
        <div className="flex justify-end">
          <ErrorLine>{failed}</ErrorLine>
        </div>
      )}
    </div>
  );
}

/** A picture's mark beside the slider: small at the smaller end. */
function ZoomMark({ small = false }: { small?: boolean }) {
  const s = small ? 12 : 16;
  return (
    <svg
      aria-hidden="true"
      width={16}
      height={16}
      viewBox="0 0 16 16"
      className="flex-none text-faint"
    >
      <rect
        x={(16 - s) / 2 + 0.5}
        y={(16 - s) / 2 + 0.5}
        width={s - 1}
        height={s - 1}
        rx={2}
        fill="none"
        stroke="currentColor"
      />
      <path
        d={
          small
            ? "M4.5 11 L7 8.5 L9 10.5 L10 9.5 L11.5 11"
            : "M2.5 13 L6.5 9 L9.5 12 L11 10.5 L13.5 13"
        }
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
      />
    </svg>
  );
}
